import {
    useCallback,
    useState,
    useMemo,
} from 'react'
import noop from 'lodash/noop'
import omit from 'lodash/omit'
import {
    useLocation,
} from 'react-router-dom'

import useFieldsWithClassName from 'app/hooks/useFieldsWithClassName'
import useHasPermission from 'app/hooks/useHasPermission'
import useGetMainCategory from 'app/hooks/useGetMainCategory'
import {
    useGetLocation,
} from 'app/Apps/ContactManagement/Locations/hooks'
import {
    ContactMainType,
} from 'app/enums/label.enums'
import useEnumValues from 'app/hooks/useEnumValues'
import getAllowedCategoriesTypes from 'app/Apps/ContactManagement/utils/getAllowedCategoriesTypes'
import useGetAllowedCategoryType from 'app/Apps/ContactManagement/utils/useGetAllowedCategoryType'
import {
    generateSkyNetLink,
} from 'app/SkyNetRoutes'
import useRelationshipsManagementRoutes from 'app/SkyNetRoutes/hooks/useRelationshipsManagementRoutes'

import DomainObjectForm from 'app/shared-components/DomainObjectForm'
import ButtonContainer from 'app/shared-components/ButtonContainer'
import Button from 'app/shared-components/Button'

import contactAccessRights from 'app/access-rights/contactAccessRights'
import {
    Contact, ContactDuplication,
} from '../Contacts.types'
import ContactDuplicationDialog from '../ContactDuplicationDialog'
import useContactDuplicationCheck from '../hooks/useContactDuplicationCheck'
import useHasContactPermissionType from '../../utils/useHasContactPermissionType'
import PROSPECT_READ_ONLY_LIST from './prospectReadOnlyList'
import useStyles from './ContactForm.style'
import contactFormFields from './ContactForm.fields'

export type ContactFormProps = {
    data?: Contact,
    onCancel?: (props?: any) => void,
    disabled?: boolean,
    action?: (data: Contact) => void,
    customButtons?: JSX.Element[],
}

const defaultProps: Partial<ContactFormProps> = {
    data: {} as Contact,
    onCancel: noop,
    disabled: false,
    customButtons: [],
}

const ContactForm = ({
    action,
    onCancel,
    disabled,
    data,
    customButtons,
}: ContactFormProps) => {
    const {
        classes,
    } = useStyles()
    const isNew = !data?.id
    const {
        state,
    } = useLocation()

    const {
        Engagements: EngagementsRoutes,
    } = useRelationshipsManagementRoutes()

    const getAllowedValues = useEnumValues()
    const contactMainTypes = getAllowedValues(ContactMainType)

    const mainCategories = useGetMainCategory()

    const permissionType = disabled ? 'read' : 'edit'
    const mainTypes = useGetAllowedCategoryType(contactAccessRights, isNew ? 'create' : permissionType)

    const onlyReadProspect = useHasPermission(PROSPECT_READ_ONLY_LIST)
    const canCreateProspect = useHasPermission(['location_sales'])
    const showEngagementLink = useHasPermission(['relationships_sales'])

    const isProspectOnLoad = useMemo(() => {
        if (isNew) {
            return !onlyReadProspect
        }
        return data?.isProspect
    }, [
        data?.isProspect,
        onlyReadProspect,
        isNew,
    ])

    const formatMainTypes = useCallback((mainType) => {
        return mainType?.map((type) => {
            return {
                value: type,
                label: contactMainTypes[type],
            }
        })
    }, [contactMainTypes])

    const initialValue = useMemo(() => {
        return {
            ...data,
            mainType: formatMainTypes(data.mainType),
            isProspect: isProspectOnLoad,
            company: isNew ? state?.company : data?.company,
        }
    }, [
        data,
        formatMainTypes,
        isNew,
        isProspectOnLoad,
        state?.company,
    ])

    const [
        duplicationData,
        setDuplicationData,
    ] = useState<ContactDuplication[] | null>(null)

    const [
        value,
        setValue,
    ] = useState(initialValue)

    const {
        isProspect,
    } = value

    const isProspectDisabled = useMemo(() => {
        return !canCreateProspect || (!isNew && !isProspect)
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const allowedMainTypes = useMemo(() => {
        if (mainCategories) {
            return getAllowedCategoriesTypes(
                mainCategories?.contactMainType,
                mainTypes,
                false,
                getAllowedValues,
            )
        }
        return []
    }, [
        mainCategories,
        mainTypes,
        getAllowedValues,
    ])

    const fields = useMemo(() => {
        return contactFormFields(
            {
                disabled,
                allowedMainTypes,
                hasRelatedUser: value?.hasRelatedUser,
                isProspectDisabled,
                isProspect,
                showEngagementLink,
                engagementLink: value?.engagementId ? generateSkyNetLink({
                    domainPath: EngagementsRoutes.Overview.path,
                    params: {
                        key: value?.engagementId,
                    },
                }) : null,
            },
        )
    }, [
        EngagementsRoutes,
        disabled,
        allowedMainTypes,
        value?.hasRelatedUser,
        isProspectDisabled,
        isProspect,
        showEngagementLink,
        value?.engagementId,
    ])

    const callAction = useCallback(() => {
        action(omit(value, [
            'mainType',
            ...isProspect ? ['location'] : ['company'],
        ]))
    }, [
        action,
        value,
        isProspect,
    ])

    const onCheckDuplicateSuccess = useCallback((duplicates: ContactDuplication[]) => {
        const duplicatesOtherThanSelf = duplicates?.filter((dup) => {
            return isNew || (dup.id !== value.id)
        }) ?? []

        if (!duplicatesOtherThanSelf?.length) {
            callAction()
            return
        }

        setDuplicationData(duplicatesOtherThanSelf)
    }, [
        isNew,
        value,
        callAction,
    ])

    const checkForDuplicates = useContactDuplicationCheck({
        onSuccess: onCheckDuplicateSuccess,
    })

    const handleSave = useCallback(() => {
        checkForDuplicates({
            contactName: value?.contactName, email: value?.email,
        })
    }, [
        checkForDuplicates,
        value?.contactName,
        value?.email,
    ])

    const handleCancel = useCallback(() => {
        onCancel?.()
        setValue(initialValue)
    }, [
        initialValue,
        onCancel,
    ])

    const closeConfirmationDialog = useCallback(() => {
        setDuplicationData(null)
    }, [setDuplicationData])

    const hasReadPermissions = useHasContactPermissionType(data.mainType)
    const hasEditPermissions = useHasContactPermissionType(data.mainType, 'edit')

    const isNotReadable = useMemo(() => {
        return !hasReadPermissions && !isNew
            && value.mainType
            && value.mainType.length
    }, [
        hasReadPermissions,
        value.mainType,
        isNew,
    ])

    const isDisabled = useMemo(() => {
        return (!hasEditPermissions && !isNew && Boolean(value.mainType)
            && Boolean(value.mainType.length)) || disabled
    }, [
        hasEditPermissions,
        value.mainType,
        isNew,
        disabled,
    ])

    const {
        data: locationData,
    } = useGetLocation(!isProspect ? value?.location?.id : undefined)

    const enhancedValue = useMemo(() => {
        return {
            ...value,
            country: isProspect ? value?.country : locationData?.country,
        }
    }, [
        locationData?.country,
        value,
        isProspect,
    ])

    const contactComponents = useFieldsWithClassName(
        fields, classes,
    )

    if (isNotReadable) {
        return <div>Access denied</div>
    }

    return (
        <div>
            <DomainObjectForm
                name="Contact"
                value={enhancedValue}
                fields={contactComponents}
                className={classes.container}
                exists={!isNew}
                onChange={setValue}
                disabled={isDisabled}
            />
            {!isDisabled && (
                <ButtonContainer>
                    <Button
                        label={isNew ? 'Create' : 'Save'}
                        onClick={handleSave}
                    />
                    {customButtons || null}
                    <Button
                        secondary
                        label="Cancel"
                        onClick={handleCancel}
                    />
                </ButtonContainer>
            )}
            <ContactDuplicationDialog
                action={callAction}
                data={duplicationData}
                close={closeConfirmationDialog}
                positiveLabel={isNew ? 'CREATE ANYWAY' : 'UPDATE ANYWAY'}
                title={isNew ? 'Creation Information' : 'Update Information'}
            />
        </div>
    )
}

ContactForm.defaultProps = defaultProps

export default ContactForm
